import { signal } from '@preact/signals';
import { isEqual, sum, uniq } from 'es-toolkit';
import { debounce } from 'es-toolkit/compat';
import { getMany, keys, setMany } from 'idb-keyval';
import { create } from 'zustand';
import {
  Episode,
  Podcast,
  PodcastId,
  PodcastOnly,
  SerializedPodcast,
} from '../models/podcasts';
import { fetchEpisode, fetchEpisodes, fetchPodcast } from '../utils/api';
import { default as debugLog } from '../utils/debug';
import { reportSentryError, reportSentryWarning } from '../utils/error-handling';
import {
  DeferredPromise,
  exposeOnWindow,
  isEpisodeIdentifier,
  nowInSeconds,
  onlyUnique,
  wait,
} from '../utils/helpers';
import { removeItemSymbol, setLazyValue } from '../utils/localstorage';
import { currentEpisode, currentPlaylist } from './audio-signals';
import { useUserDataStore } from './user-data';

type PodcastsStore = {
  podcasts: Partial<Record<PodcastId, Podcast>>;
  sessionPodcastIds: Set<PodcastId>; // Keep??? Not currently used
};

type DataSource = 'API' | 'IndexedDB';

type PodcastStoreActions = {
  addSessionPodcastId: (podcastId: PodcastId) => void;
  updatePodcast: (data: PodcastOnly, source: DataSource) => void;
  addOrUpdateEpisodes: (
    podcastId: PodcastId,
    episodes: Episode[],
    source: DataSource
  ) => void;
  addOrUpdateEpisode: (podcastId: PodcastId, episode: Episode) => void;
  idToEpisode: (id: string) => Episode | undefined;
};

const log = (...args: any[]) => {
  debugLog({ logName: 'podcasts', logColor: 'darkgreen' }, ...args);
};

// IndexedDB startup (resolves with list of all keys if successful)
export const idbStartupPromise = new DeferredPromise<string[] | undefined>();
export const idbStartupCompleteSignal = signal<boolean>(false);
const isIdbStartup = (source: DataSource) =>
  source === 'IndexedDB' && !idbStartupPromise.isSettled;

export const usePodcastsStore = create<
  PodcastsStore & { actions: PodcastStoreActions }
>()((set, get) => ({
  podcasts: {},
  sessionPodcastIds: new Set(),
  actions: {
    addSessionPodcastId: (podcastId) => {
      const { sessionPodcastIds } = get();
      sessionPodcastIds.add(podcastId);
    },
    updatePodcast: (podcast, source: DataSource) => {
      const { id } = podcast;
      const { podcasts } = get();
      const oldPodcast = podcasts[id] as Podcast | undefined;
      const newPodcast: Podcast = {
        ...podcast,
        description: podcast.description || oldPodcast?.description, // Keep description if new podcast doesn't have it set
        episodes: oldPodcast?.episodes || [], // Preserve existing episodes
      };

      if (isEqual(oldPodcast, newPodcast)) return;

      if (oldPodcast) {
        log(
          source,
          'Updated podcast',
          oldPodcast.name,
          Object.entries(oldPodcast).reduce((diff, [key, value]) => {
            // @ts-ignore
            if (!isEqual(value, newPodcast[key])) {
              // @ts-ignore
              return { ...diff, [key]: [value, newPodcast[key]] };
            }
            return diff;
          }, {})
        );
      } else if (!isIdbStartup(source)) {
        // No need to log initial population of podcasts from IndexedDB
        log(source, 'Added podcast', newPodcast.name, newPodcast.id);
      }

      set({
        podcasts: {
          ...podcasts,
          [id]: newPodcast,
        },
      });
    },
    addOrUpdateEpisodes: (podcastId, newEpisodes, source) => {
      if (!newEpisodes.length) {
        log(source, 'No episodes to update', podcastId);
        return;
      }
      if (!newEpisodes[0].id) throw new Error('Episode missing ID'); // Can happen if we forget to un-serialize episodes

      const { podcasts } = get();
      const podcast = podcasts[podcastId];
      if (!podcast) {
        // log(source, 'No podcast to update', podcastId);
        return;
      }

      let allEpisodes: Episode[] = [];
      let actions: string;
      let shouldLog = true;

      if (!podcast.episodes.length) {
        allEpisodes = newEpisodes;
        actions = `Inserted ${newEpisodes.length} episodes`;
        if (isIdbStartup(source)) shouldLog = false;
      } else {
        const merged = new Map<string, Episode>();
        for (const record of podcast.episodes) {
          merged.set(record.id, record);
        }

        let insertCount = 0;
        let updateCount = 0;
        for (const record of newEpisodes) {
          const existing = merged.get(record.id);
          if (!existing) {
            insertCount++;
            merged.set(record.id, record);
          } else if (record.queriedAt > existing.queriedAt) {
            // It's expected for the passed-in episodes to be newer (greater .queriedAt),
            // but we only bother to swap them out if we already know that we're going to
            // be inserting new episodes -or- if the new episode has a different hash.
            // Otherwise, we just keep the existing episode. FWIW, we could always compare
            // hashes but this is slightly more performant since we generate hashes lazily.
            if (insertCount || record.hash !== existing.hash) {
              updateCount++;
              merged.set(record.id, record);
            }
          }
        }
        if (!insertCount && !updateCount) {
          // log(source, 'No episodes need updating', podcast.name);
          return;
        }

        // Sort by pubDate, newest first
        allEpisodes = Array.from(merged.values()).sort((a, b) =>
          a.pubDate > b.pubDate ? -1 : 1
        );

        const skippedCount = newEpisodes.length - insertCount - updateCount;
        actions = [
          insertCount ? `Added ${insertCount} episodes` : null,
          updateCount ? `Updated ${updateCount} episodes` : null,
          skippedCount ? `Skipped ${skippedCount} episodes` : null,
        ]
          .filter(Boolean)
          .join(', ');
      }

      if (shouldLog)
        log(
          source,
          actions,
          podcast.name,
          podcast.episodes.length,
          '->',
          allEpisodes.length
        );

      // Update currentEpisode signal if underlying data changed
      const currentEpisodeValue = currentEpisode.value;
      if (currentEpisodeValue && currentEpisodeValue.podcastId === podcastId) {
        const matchingNewEpisode = allEpisodes.find(
          (ep) => ep.guidHash === currentEpisodeValue.guidHash
        );
        if (matchingNewEpisode) currentEpisode.value = matchingNewEpisode;
      }

      set({
        podcasts: {
          ...podcasts,
          [podcastId]: Object.assign({}, podcast, { episodes: allEpisodes }),
        },
      });
    },
    addOrUpdateEpisode: (podcastId, episode) => {
      const { podcasts } = get();
      const podcast = podcasts[podcastId];
      if (!podcast) {
        log('No podcast to update', podcastId);
        return;
      }

      // Swap out old episode with new one
      let allEpisodes = podcast.episodes.slice();

      let wasUpdated = false;

      const index = allEpisodes.findIndex((ep) => ep.guidHash === episode.guidHash);
      if (index === -1) {
        // Add new episode and sort by date (newest first)
        allEpisodes.push(episode);
        allEpisodes.sort((a, b) => (a.pubDate > b.pubDate ? -1 : 1));
        wasUpdated = true;
      } else if (
        episode.queriedAt > (allEpisodes[index].queriedAt || 0) &&
        episode.hash !== allEpisodes[index].hash
      ) {
        allEpisodes.splice(index, 1, episode);
        wasUpdated = true;
        // Update currentEpisode to reflect change in audio player, lock screen, etc.
        if (currentEpisode.value && currentEpisode.value.id === episode.id) {
          currentEpisode.value = episode;
        }
        // We no longer store descriptions in this store...
        // } else if (episode.description && !allEpisodes[index].description) {
        //   // Just update description since we don't store that in LocalStorage anymore
        //   allEpisodes[index].description = episode.description;
        //   wasUpdated = true;
        //   // Update currentEpisode to reflect change in audio player, lock screen, etc.
        //   if (currentEpisode.value && currentEpisode.value.id === episode.id) {
        //     currentEpisode.value.description = episode.description; // Hmm, not sure if this will cause a re-render...
        //   }
      }

      if (!wasUpdated) return;

      log('Updated single episode', episode.id, episode.title);

      set({
        podcasts: {
          ...podcasts,
          [podcastId]: Object.assign({}, podcast, { episodes: allEpisodes }),
        },
      });
    },
    idToEpisode: (id: string) => {
      if (!isEpisodeIdentifier(id)) {
        throw new Error(`Invalid episode compound id: ${id}`);
      }
      const [podcastId, guidHash] = id.split('|');
      const podcast = get().podcasts[podcastId];
      if (podcast) {
        return podcast.episodes.find((ep) => ep.guidHash === guidHash);
      }
    },
  },
}));

//
// Expose actions
//

export const podcastActions = usePodcastsStore.getState().actions;
const {
  addOrUpdateEpisodes,
  updatePodcast,
  addOrUpdateEpisode,
  addSessionPodcastId,
  idToEpisode,
} = podcastActions;
export {
  addOrUpdateEpisode,
  addOrUpdateEpisodes,
  addSessionPodcastId,
  idToEpisode,
  updatePodcast,
};

//
// Set initial values from IndexedDB and save updates to IndexedDB
//

async function getIdbKeys(): Promise<string[]> {
  const keyStartTime = Date.now();
  const idbKeys = await keys();
  log('Got', idbKeys.length, 'IDB keys in', Date.now() - keyStartTime, 'ms');

  return idbKeys.filter((key): key is string => {
    // IDB keys can be all sorts of data types, but we only expect strings
    if (typeof key === 'string') return true;

    reportSentryWarning(new Error(`Unexpected IDB key type: ${typeof key}`), { key });
    return false;
  });
}

async function getPodcastsFromIndexedDB() {
  const idbKeys = await getIdbKeys();
  const podcastKeys = idbKeys.filter((key) => /^podcast-\d+$/.test(key));

  if (!podcastKeys.length) {
    log('No podcasts found in IndexedDB');
    return { podcasts: [], idbKeys };
  }

  const podcastsStartTime = Date.now();
  const podcasts = (await getMany(podcastKeys)) as SerializedPodcast[];
  const totalEpisodeCount = sum(podcasts.map((p) => p.episodes.length));
  log(
    'Got',
    podcasts.length,
    'podcasts from IndexedDB in',
    Date.now() - podcastsStartTime,
    'ms',
    '(' + totalEpisodeCount + ' episodes)'
  );

  return { podcasts, idbKeys };
}

if (typeof window !== 'undefined') {
  const updatePodcastAndEpisodes = (podcasts: SerializedPodcast[]) => {
    podcasts.forEach((podcast) => {
      updatePodcast(podcast, 'IndexedDB');
      addOrUpdateEpisodes(
        podcast.id,
        podcast.episodes.map((ep) => new Episode(ep, podcast)),
        'IndexedDB'
      );
    });
  };

  // Attempt to get initial podcasts from IndexedDB and update store in under 5 seconds
  // (typically under 1s) so that we can set initial episode right away (ID stored in LocalStorage)
  // as well as populate the rest of the UI even when offline or on a slow network.
  // If we don't make the 5s threshold, there won't be an initial episode, but eventually
  // when IndexedDB catches up, the UI will update, filling in any missing episodes.
  const timeoutSeconds = 5;
  Promise.race([
    getPodcastsFromIndexedDB()
      .then((result) => {
        updatePodcastAndEpisodes(result.podcasts);
        return result;
      })
      .catch((error) => {
        reportSentryError(
          new Error('Failed to get podcasts from IDB', { cause: error })
        );
        return { idbKeys: undefined };
      }),
    wait(timeoutSeconds * 1000).then(() => 'timeout' as const),
  ])
    .then((result) => {
      if (result === 'timeout') {
        return reportSentryWarning(
          new Error(`getPodcastsFromIndexedDB very slow (> ${timeoutSeconds}s)`)
        );
      }
      // Start saving updates to IndexedDB
      usePodcastsStore.subscribe(saveMostPodcastsToIndexedDB);
      // Resolve with idbKeys
      idbStartupPromise.resolve(result.idbKeys);
    })
    .finally(() => {
      // Fallback if IndexedDB startup takes too long or fails unexpectedly
      idbStartupPromise.resolve(undefined);
      // Signal that IndexedDB startup is complete
      // Since it's in a .finally() will happen a microtask after
      // the Promise resolves (if successful) which is nice since it
      // allows other side effects (e.g. initial episode selection)
      // to run first
      idbStartupCompleteSignal.value = true;
    });
}

const saveMostPodcastsToIndexedDB = debounce((store: PodcastsStore) => {
  const { podcasts } = store;
  // Persist all podcasts and episodes to IndexedDB that user is likely to interact
  // with in a future session
  const {
    followedPodcastIds,
    downloadedEpisodeIds,
    recentEpisodeIds,
    listenLaterEpisodeIds,
  } = useUserDataStore.getState();
  const mostRecentEpisodeIds = recentEpisodeIds.slice(0, 50);
  const podcastIdsToStore: PodcastId[] = uniq([
    ...followedPodcastIds,
    ...downloadedEpisodeIds.map((compoundId) => compoundId.split('|')[0] as PodcastId),
    ...listenLaterEpisodeIds.map((compoundId) => compoundId.split('|')[0] as PodcastId),
    ...mostRecentEpisodeIds.map((compoundId) => compoundId.split('|')[0] as PodcastId),
  ]);
  const keyValuePairs: [`podcast-${PodcastId}`, Podcast][] = [];
  for (const podcastId of podcastIdsToStore) {
    if (podcasts[podcastId]) {
      keyValuePairs.push([
        ('podcast-' + podcastId) as `podcast-${PodcastId}`,
        podcasts[podcastId],
      ]);
    }
  }
  const startTime = Date.now();
  setMany(keyValuePairs).then(() => {
    log(
      'Saved',
      keyValuePairs.length,
      'podcasts to IndexedDB in',
      Date.now() - startTime,
      'ms'
    );
  });
}, 1000);

// Cleanup old podcasts in LocalStorage
setLazyValue('PodcastsStore__podcasts', removeItemSymbol);

//
// Fetch missing podcasts & episodes
// These were probably added/listened-to on another device
//

const checkForMissingPodcastsAndEpisodes = debounce(() => {
  const { podcasts } = usePodcastsStore.getState();
  const { followedPodcastIds, downloadedEpisodeIds, recentEpisodeIds } =
    useUserDataStore.getState();
  const playlistEpisodeIds = currentPlaylist.value.map(([id]) => id);

  const missingPodcastsAndEpisodes: {
    podcastId: PodcastId; // Podcast that might need to be fetched
    episodeCompoundIds: string[]; // Episodes that need to be fetched
    isPodcastMissing: boolean;
  }[] = [];

  const updateMissing = (podcastId: PodcastId, episodeCompoundIds: string[]) => {
    const matchingObject = missingPodcastsAndEpisodes.find(
      (obj) => obj.podcastId === podcastId
    );
    if (matchingObject) {
      matchingObject.episodeCompoundIds.push(...episodeCompoundIds);
    } else {
      missingPodcastsAndEpisodes.push({
        podcastId,
        isPodcastMissing: !podcasts[podcastId],
        episodeCompoundIds,
      });
    }
  };

  followedPodcastIds.forEach((podcastId) => {
    if (!podcasts[podcastId]) updateMissing(podcastId, []);
  });

  [...downloadedEpisodeIds, ...playlistEpisodeIds, ...recentEpisodeIds.slice(0, 20)]
    .filter(onlyUnique)
    .forEach((compoundId) => {
      const parts = compoundId.split('|');
      const podcastId = parts[0] as PodcastId;
      const podcast = podcasts[podcastId];
      if (!podcast || !podcast.episodes.some((ep) => ep.guidHash === parts[1])) {
        updateMissing(podcastId, [compoundId]);
      }
    });

  missingPodcastsAndEpisodes.forEach(
    ({ podcastId, isPodcastMissing, episodeCompoundIds }) => {
      fetchMissingPodcastAndEpisodes(podcastId, isPodcastMissing, episodeCompoundIds);
    }
  );
}, 1000);

// Fetch podcasts that are added to favorites but not yet in this store
idbStartupPromise.promise.then(() => {
  useUserDataStore.subscribe(checkForMissingPodcastsAndEpisodes);
  checkForMissingPodcastsAndEpisodes();
});

const missingPodcastIdsBeingFetched = new Set<PodcastId>();
async function fetchMissingPodcastAndEpisodes(
  id: PodcastId,
  isPodcastMissing: boolean,
  episodeIds: string[]
) {
  if (missingPodcastIdsBeingFetched.has(id)) return;

  missingPodcastIdsBeingFetched.add(id);

  if (isPodcastMissing) {
    log('Fetching missing podcast', id);
    try {
      await fetchPodcast(id);
    } catch (error) {
      reportSentryError(error, { context: 'Failed to fetch missing podcast: ' + id });

      // Without podcast we shouldn't fetch episodes, so return early and try again later
      missingPodcastIdsBeingFetched.delete(id);
      return;
    }
  }

  if (episodeIds.length) {
    // Fetch first page of episodes if episodes haven't been queried in over a day
    const { podcasts } = usePodcastsStore.getState();
    const { episodes } = podcasts[id]!;
    if (!episodes.length || nowInSeconds() - episodes[0].queriedAt > 60 * 60 * 24) {
      try {
        log('Fetching all episodes for podcast w/ missing episodes', id);
        await fetchEpisodes(id, 'all');
      } catch (error) {
        reportSentryError(error, { context: 'Failed to fetch all episodes ' + id });
      }
    }
    const missingEpisodeIds = episodeIds.filter((id) => !idToEpisode(id));
    if (missingEpisodeIds.length) {
      try {
        log('Fetching still-missing episodes for podcast', missingEpisodeIds);
        await Promise.all(
          missingEpisodeIds.map((compoundId) =>
            fetchEpisode(id, compoundId.split('|')[1])
          )
        );
      } catch (error) {
        reportSentryError(error, {
          context: 'Failed to still-missing episodes for podcast' + id,
        });
      }
    }
  }

  missingPodcastIdsBeingFetched.delete(id);
}

exposeOnWindow({
  // Make available to audio-player.ts w/out circular reference
  globalPodcastActions: podcastActions,
  // for debugging
  _usePodcastsStore: usePodcastsStore,
});
